import type { Map as MapboxMap } from 'mapbox-gl';
import { Popup } from 'mapbox-gl';
import { type RefObject, useEffect, useState } from 'react';
import type { SelectedZone } from './useZoneInteractions';

/**
 * Manage the Mapbox Popup + portal container for the selected hunting zone.
 *
 * When `selectedZone` is non-null, a Mapbox `Popup` is anchored at the zone's
 * lng/lat (so it tracks pan/zoom natively) and a DOM container is allocated for
 * the caller to render `ZoneMapPopup` into via `createPortal` — no innerHTML,
 * no setHTML, keeping all content XSS-safe as React text nodes.
 *
 * Returns the portal container (or null when no popup is open).
 */
export function useZonePopup(
  map: RefObject<MapboxMap | null>,
  selectedZone: SelectedZone | null,
  onClose: () => void,
): HTMLDivElement | null {
  // DOM container for the portal, held in STATE (not a ref). This is
  // load-bearing: publishing the freshly-created container via setState forces
  // a re-render in the same commit, so the caller's `createPortal` mounts into
  // the *live* popup. A ref would not re-render — the portal would lag a render
  // behind the popup and, on reopen, target the previous (detached) container,
  // leaving the user with an empty/invisible popup until an unrelated re-render
  // (e.g. a zoom) happened to repaint it.
  const [popupContainer, setPopupContainer] = useState<HTMLDivElement | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: map is a stable ref; onClose always performs the same clear (its identity may change across renders but the effect intentionally reacts only to selectedZone)
  useEffect(() => {
    const currentMap = map.current;

    if (!selectedZone || !currentMap) {
      // No zone selected (or cleared) — nothing to anchor.
      setPopupContainer(null);
      return;
    }

    // Allocate a fresh portal container for this selection.
    const container = document.createElement('div');

    const popup = new Popup({
      // ZoneMapPopup renders its own card + close button; suppress the native
      // chrome (closeButton + white content box/tip) and strip the wrapper via
      // the shared `vanilla-popup` class, matching useStationsInteractions.
      closeButton: false,
      closeOnClick: true,
      closeOnMove: false,
      maxWidth: 'none',
      className: 'vanilla-popup',
    });

    // setDOMContent hands a live DOM node to the Popup; React renders into it
    // via createPortal — zero innerHTML, zero XSS surface.
    popup.setDOMContent(container).setLngLat([selectedZone.lngLat.lng, selectedZone.lngLat.lat]);
    popup.addTo(currentMap);

    // Publish the container so the portal mounts into this popup on the next
    // render. Tracks the live popup 1:1 with the rendered content.
    setPopupContainer(container);

    // When Mapbox closes the popup natively (e.g. closeOnClick on map background),
    // sync React state. `removed` guards against the loop where onClose →
    // selectedZone null → effect cleanup → popup.remove() → 'close' fires again.
    let removed = false;
    popup.on('close', () => {
      if (!removed) {
        removed = true;
        onClose();
      }
    });

    return () => {
      // Tear down the popup without re-triggering onClose (the `removed` guard
      // absorbs the 'close' event that Popup.remove() fires). We deliberately
      // do NOT setPopupContainer(null) here: on a zone→zone switch the next
      // effect body sets the new container, and nulling it first would make the
      // live container depend on update batching. The container is reset to null
      // only by the early-return branch above (i.e. when no zone is selected).
      removed = true;
      popup.remove();
    };
  }, [selectedZone]);

  return popupContainer;
}
